/**
 * Review List Component
 * Displays rating summary and paginated reviews with user's own review management
 */

import { useState } from "react";
import { ReviewCard } from "./ReviewCard";
import { ReviewForm } from "./ReviewForm";
import { StarRating } from "./StarRating";
import { cn } from "@/utils/cn";
import type {
  RatingSummary,
  ReviewListResponse,
  MyReview,
} from "@/types/tools";

interface ReviewListProps {
  /** Tool ID */
  toolId: string;
  /** Rating summary for the tool */
  ratingSummary: RatingSummary | undefined;
  /** Paginated reviews */
  reviews: ReviewListResponse | undefined;
  /** Current user's review */
  myReview: MyReview | null | undefined;
  /** Loading states */
  isLoading?: boolean;
  isLoadingReviews?: boolean;
  /** Callbacks */
  onCreateReview: (data: {
    rating: number;
    reviewTitle?: string;
    reviewText?: string;
  }) => void;
  onUpdateReview: (data: {
    rating?: number;
    reviewTitle?: string;
    reviewText?: string;
  }) => void;
  onDeleteReview: () => void;
  onPageChange: (page: number) => void;
  /** Current page */
  currentPage: number;
  /** Mutation states */
  isCreating?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  /** Additional className */
  className?: string;
}

export function ReviewList({
  ratingSummary,
  reviews,
  myReview,
  isLoading,
  isLoadingReviews,
  onCreateReview,
  onUpdateReview,
  onDeleteReview,
  onPageChange,
  currentPage,
  isCreating,
  isUpdating,
  isDeleting,
  className,
}: ReviewListProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleEdit = () => setIsEditing(true);
  const handleCancelEdit = () => setIsEditing(false);

  const handleSubmitEdit = (data: {
    rating: number;
    reviewTitle?: string;
    reviewText?: string;
  }) => {
    onUpdateReview(data);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDeleteReview();
    setShowDeleteConfirm(false);
  };

  if (isLoading) {
    return (
      <div className={cn("animate-pulse space-y-4", className)}>
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Rating Summary */}
      {ratingSummary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Ratings & Reviews
          </h3>

          <div className="flex items-start gap-8">
            {/* Average Rating */}
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                {ratingSummary.averageRating?.toFixed(1) ?? "-"}
              </div>
              <StarRating
                value={ratingSummary.averageRating ?? 0}
                size="md"
                className="mt-2 justify-center"
              />
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {ratingSummary.ratingCount} review
                {ratingSummary.ratingCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Rating Distribution */}
            <div className="flex-1 space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count =
                  ratingSummary.ratingDistribution[
                    star as keyof typeof ratingSummary.ratingDistribution
                  ] ?? 0;
                const percentage =
                  ratingSummary.ratingCount > 0
                    ? (count / ratingSummary.ratingCount) * 100
                    : 0;

                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-3">
                      {star}
                    </span>
                    <StarRating value={1} size="sm" />
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 w-8">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* User's Review Section */}
      <div>
        {myReview && !isEditing ? (
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              Your Review
            </h4>
            <ReviewCard
              review={myReview}
              isOwn
              onEdit={handleEdit}
              onDelete={() => setShowDeleteConfirm(true)}
            />

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                  Are you sure you want to delete your review? This cannot be
                  undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : isEditing && myReview ? (
          <ReviewForm
            existingReview={myReview}
            onSubmit={handleSubmitEdit}
            onCancel={handleCancelEdit}
            isSubmitting={isUpdating}
          />
        ) : !myReview ? (
          <ReviewForm onSubmit={onCreateReview} isSubmitting={isCreating} />
        ) : null}
      </div>

      {/* Reviews List */}
      {reviews && reviews.reviews.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
            All Reviews
          </h4>

          {isLoadingReviews ? (
            <div className="animate-pulse space-y-4">
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {reviews.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {reviews.totalPages}
              </span>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= reviews.totalPages}
                className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {reviews && reviews.reviews.length === 0 && !myReview && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No reviews yet. Be the first to review this tool!</p>
        </div>
      )}
    </div>
  );
}
